import React from "react";
import {
  FaCheck,
  FaEdit,
  FaLock,
  FaPlus,
  FaRegSquare,
  FaTrash,
} from "react-icons/fa";
import { GiBlackBelt } from "react-icons/gi";

import {
  canAccessDanPanel,
  getBeltName,
  getDanProgress,
  toBoolean,
} from "../utils/passageGradeUtils";

function UVCard({ label, checked, description }) {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
            checked
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200"
              : "bg-slate-100 text-slate-400 dark:bg-white/10 dark:text-white/40"
          }`}
        >
          {checked ? <FaCheck /> : <FaRegSquare />}
        </div>

        <div>
          <p className="text-sm font-bold text-slate-950 dark:text-white">
            {label}
          </p>

          {description && (
            <p className="mt-1 text-xs font-medium leading-5 text-slate-500 dark:text-white/45">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function DanGradePanel({
  lastGrade,
  gradeCN,
  creating,
  deleting,
  onCreate,
  onEdit,
  onDelete,
}) {
  const isEligible = canAccessDanPanel(lastGrade);
  const progress = getDanProgress(gradeCN);

  return (
    <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
      <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 p-5 text-white sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[1.25rem] bg-white/15 text-3xl backdrop-blur-xl">
              <GiBlackBelt />
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/50">
                Ceinture noire / Dans
              </p>

              <h2 className="mt-2 font-titre text-3xl font-bold">
                Validation des UV
              </h2>

              <p className="mt-2 max-w-2xl text-sm font-medium leading-6 text-white/65">
                Espace de suivi pour UV1, UV2, UV3, UV4, dominante et points
                marqués en shiai.
              </p>
            </div>
          </div>

          <div className="rounded-[1.5rem] bg-white/10 p-4 backdrop-blur-xl">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/50">
              Progression
            </p>

            <p className="mt-1 text-3xl font-bold">
              {progress.completed}/{progress.total}
            </p>

            <div className="mt-3 h-2 w-48 overflow-hidden rounded-full bg-white/15">
              <div
                className="h-full rounded-full bg-orange-400 transition-all"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {!isEligible ? (
        <div className="p-6">
          <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-white/10 dark:bg-white/[0.03]">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[1.5rem] bg-white text-slate-500 shadow-sm dark:bg-white/10 dark:text-white/50">
              <FaLock className="text-2xl" />
            </div>

            <h3 className="mt-4 font-titre text-2xl font-bold text-slate-950 dark:text-white">
              Espace UV verrouillé
            </h3>

            <p className="mx-auto mt-2 max-w-xl text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
              Le judoka doit être ceinture marron ou supérieure pour accéder au
              suivi des UV. Ceinture actuelle :{" "}
              <span className="font-bold">
                {lastGrade ? getBeltName(lastGrade) : "non renseignée"}
              </span>
              .
            </p>
          </div>
        </div>
      ) : !gradeCN ? (
        <div className="p-6">
          <div className="rounded-[1.5rem] border border-orange-200 bg-orange-50 p-6 dark:border-orange-400/20 dark:bg-orange-500/10">
            <h3 className="font-titre text-2xl font-bold text-slate-950 dark:text-white">
              Créer le suivi des UV
            </h3>

            <p className="mt-2 text-sm font-medium leading-7 text-slate-600 dark:text-white/60">
              Le judoka est éligible. Tu peux créer son espace de validation
              pour suivre les UV nécessaires aux passages de Dans.
            </p>

            <button
              type="button"
              onClick={onCreate}
              disabled={creating}
              className="mt-5 inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-orange-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
            >
              <FaPlus />
              {creating ? "Création..." : "Créer l'espace UV"}
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 sm:p-6">
          <div className="grid gap-4 lg:grid-cols-4">
            <UVCard
              label="UV1"
              checked={toBoolean(gradeCN.uv1)}
              description="Kata"
            />
            <UVCard
              label="UV2"
              checked={toBoolean(gradeCN.uv2)}
              description="Technique"
            />
            <UVCard
              label="UV3"
              checked={toBoolean(gradeCN.uv3)}
              description={`${Number(gradeCN.uv3_points || 0)} point(s)`}
            />
            <UVCard
              label="UV4"
              checked={toBoolean(gradeCN.uv4)}
              description="Engagement"
            />
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-[0.7fr_1.3fr]">
            <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Dominante
              </p>

              <p className="mt-2 text-lg font-bold text-slate-950 dark:text-white">
                {gradeCN.dominante || "Non définie"}
              </p>
            </div>

            <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Observations
              </p>

              <p className="mt-2 text-sm font-medium leading-7 text-slate-600 dark:text-white/60">
                {gradeCN.observations || "Aucune observation renseignée."}
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-2 border-t border-slate-200 pt-5 dark:border-white/10 sm:grid-cols-2">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5"
            >
              <FaEdit />
              Modifier le suivi
            </button>

            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
            >
              <FaTrash />
              {deleting ? "Suppression..." : "Supprimer"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default DanGradePanel;
